/* eslint-disable prettier/prettier */
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { flightsExchangeParameters } from 'action-creators/flights';
import DateRangePicker from 'components/DateRangePicker';
import { RenderProps as BookingSelectRenderProps } from 'components/Selects/BookingSelectDate';
import SelectDate from 'components/Selects/FlightsExchange/SelectDate';
import SelectLocation from 'components/Selects/FlightsExchange/SelectLocation';
import AirportSearch from 'containers/AirportsSearch';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { Airport } from 'models/airport';
import { TravelRequested } from 'models/travelRequested';
import ResponseScreen, { BackgroundTypes, IconTypes } from 'screens/components/ResponseScreen';
//import { amplitudelogEvent, SearchEvent } from 'services/amplitudeService';
//import { amplitudeEvents } from 'utils/amplitudeEvents';
import { dateFormatWeekend /* daysCount */ } from 'utils/generic';

import {
  Container,
  Fields,
  Field,
  SearchButton,
  SearchButtonContainer,
  Title,
  Spacer,
  IconCalendar,
  IconOrigin,
  IconPin,
} from './styles';

interface Props {
  defaultDepartureCode?: string;
  defaultArrivalCode?: string;
  defaultDepartureDate?: Date;
  defaultArrivalDate?: Date;
  defaultDepartureCity?: string;
  defaultArrivalCity?: string;
  changeDeparture?: boolean;
  changeArrival?: boolean;
  originalFlight?: TravelRequested;
  onSubmit: () => void;
}

export const getBookingSelectValue = (airport: Airport | undefined) => {
  if (!airport) return;

  return {
    name: airport.code,
    detail: `${airport.related_city}, ${airport.country}`,
  };
};

const FlightsExchangeSearch = ({
  defaultDepartureCode,
  defaultDepartureCity,
  defaultDepartureDate,
  defaultArrivalCode,
  defaultArrivalCity,
  defaultArrivalDate,
  changeDeparture,
  changeArrival,
  originalFlight,
  onSubmit,
}: Props) => {
  const history = useHistory();
  const location = useLocation<{ showNoResults?: boolean } | null>();
  const { t } = useStoreTranslation();
  const dispatch = useDispatch();

  const [showNoResults, setShowNoResults] = useState(location.state?.showNoResults || false);
  const [departureCity, setDepartureCity] = useState<string | undefined>(defaultDepartureCity);
  const [departureCode, setDepartureCode] = useState<string | undefined>(defaultDepartureCode);
  const [arrivalCity, setArrivalCity] = useState<string | undefined>(defaultArrivalCity);
  const [arrivalCode, setArrivalCode] = useState<string | undefined>(defaultArrivalCode);
  const [departureDate, setDepartureDate] = useState<Date | undefined>(defaultDepartureDate);
  const [arrivalDate, setArrivalDate] = useState<Date | undefined>(defaultArrivalDate);

  useEffect(() => {
    history.replace({ ...history.location, state: undefined });
  }, [history]);

  const isValid = useMemo(
    () => Boolean(departureCode && arrivalCode && departureDate),
    [departureCode, arrivalCode, departureDate],
  );
  const handleChangeDateSelect = (dates: { initialDate: Date; finalDate: Date }) => {
    setDepartureDate(dates?.initialDate);
    setArrivalDate(dates?.finalDate);
  };

  const handleClickSearchButton = useCallback(async () => {
    if (departureCode && departureDate && arrivalCode) {
      await dispatch(
        flightsExchangeParameters({
          departureCity,
          departureCode,
          departureDate,
          arrivalCity,
          arrivalCode,
          arrivalDate,
          originalFlight,
        }),
      );
    }

    //Amplitude
    /* const event: SearchEvent = {
      TRAVEL_PRODUCT_TYPE: 'FLIGHTS',
      TRAVEL_FROM: departurePoint?.code ?? '',
      TRAVEL_TO: arrivalPoint?.code ?? '',
      TRAVEL_DESTINATION_TYPE: '',
      TRAVEL_TRIP_TYPE: roundTrip ? 'true' : 'false',
      TRAVEL_ADVANCED_PURCHASE: daysCount(new Date(), departureDate).toString(),
      TRAVEL_DATE_FROM: departureDate ? new Date(departureDate?.toString()).toString() : '',
      TRAVEL_DATE_TO: returnDate ? new Date(returnDate?.toString()).toString() : '',
      TRAVEL_TRIP_LENGTH: (daysCount(departureDate, returnDate) + 1).toString(),
      TRAVEL_PASSENGERS: '1',
      TRAVEL_ADULTS: '1',
      TRAVEL_CHILDREN: '0',
      TRAVEL_INFANTS: '0',
      TRAVEL_CLASS_OF_SERVICE: cabin ?? '',
      TRAVEL_ROOMS: '',
      TRAVEL_WITH_BAGGAGE: baggageIncluded ? 'true' : 'false',
      TRAVEL_BRANCH_CODE: process.env.REACT_APP_RAPPI_BRANCH_CODE ?? '',
      SEARCHID: '',
    };

    amplitudelogEvent(amplitudeEvents.SELECT_TRAVEL_SEARCH, event, user); */

    onSubmit();
  }, [
    departureCode,
    departureDate,
    arrivalCode,
    onSubmit,
    dispatch,
    departureCity,
    arrivalCity,
    arrivalDate,
    originalFlight,
  ]);

  const renderDeparturePointSelect = () => (
    <SelectLocation
      label={t('flightsExchange.search.departure')}
      detail={t('flightsSearch.departurePoint.detail')}
      value={{
        name: `${departureCity} (${departureCode})`,
        detail: t('flightsExchange.search.departure'),
      }}
      onChange={(value) => {
        setDepartureCity((value as Airport).related_city || (value as Airport).description);
        setDepartureCode((value as Airport).code);
      }}
      icon={<IconOrigin />}
      testId="flight-search-departure-location"
    >
      {(selectProps: BookingSelectRenderProps) => (
        <AirportSearch
          type="DEPARTURE"
          {...selectProps}
          placeHolder={t('flightsSearch.departurePoint.modalSearchPlaceholder')}
          zIndex={11}
        />
      )}
    </SelectLocation>
  );

  const renderArrivalPointSelect = () => (
    <SelectLocation
      label={t('flightsExchange.search.arrival')}
      detail={t('flightsSearch.arrivalPoint.detail')}
      value={{
        name: `${arrivalCity} (${arrivalCode})`,
        detail: t('flightsExchange.search.arrival'),
      }}
      onChange={(value) => {
        setArrivalCity((value as Airport).related_city || (value as Airport).description);
        setArrivalCode((value as Airport).code);
      }}
      icon={<IconPin />}
      testId="flight-search-arrival-location"
    >
      {(selectProps: BookingSelectRenderProps) => (
        <AirportSearch
          type="ARRIVAL"
          {...selectProps}
          placeHolder={t('flightsSearch.arrivalPoint.modalSearchPlaceholder')}
          zIndex={11}
        />
      )}
    </SelectLocation>
  );

  const renderDepartureDateSelect = () => (
    <SelectDate
      label={t('flightsExchange.search.departureDate')}
      value={dateFormatWeekend(departureDate)}
      onChange={handleChangeDateSelect}
      icon={<IconCalendar />}
      testId="flight-search-departure-date"
    >
      {(selectProps: BookingSelectRenderProps) => (
        <DateRangePicker
          {...selectProps}
          type="trip"
          onlyDate={originalFlight.travelAirStretch === 'Direct'}
          currentInitialDate={departureDate}
          currentFinalDate={arrivalDate}
          zIndex={11}
        />
      )}
    </SelectDate>
  );

  const renderReturnDateSelect = () => (
    <SelectDate
      disabled={false}
      label={t('flightsExchange.search.arrivalDate')}
      value={dateFormatWeekend(arrivalDate)}
      onChange={handleChangeDateSelect}
      icon={<IconCalendar />}
      testId="flight-search-return-date"
    >
      {(selectProps: BookingSelectRenderProps) => (
        <DateRangePicker
          {...selectProps}
          type="trip"
          onlyDate={originalFlight.travelAirStretch === 'Direct'}
          currentInitialDate={departureDate}
          currentFinalDate={arrivalDate}
          editingInitialDate={false}
          canEditingInitialDate={!changeDeparture ? false : true}
          minDate={!changeDeparture ? departureDate : new Date()}
          zIndex={11}
        />
      )}
    </SelectDate>
  );

  return (
    <>
      <Container>
        <Title>{t('flightsExchange.search.title')}</Title>
        <Fields>
          <Field>{renderDeparturePointSelect()}</Field>
          <Field>{renderArrivalPointSelect()}</Field>
          <Spacer />
          {changeDeparture && <Field>{renderDepartureDateSelect()}</Field>}
          {changeArrival && <Field>{renderReturnDateSelect()}</Field>}
        </Fields>
        <SearchButtonContainer>
          <SearchButton
            disabled={!isValid}
            onClick={handleClickSearchButton}
            value={t('flightsSearch.searchButton')}
            testId="flight-search-submit"
          />
        </SearchButtonContainer>
      </Container>

      <ResponseScreen
        show={showNoResults}
        text=""
        subText={t('flightsSearch.searchError')}
        icon={IconTypes.Airplane}
        background={BackgroundTypes.Cloud}
        buttonOnClick={() => setShowNoResults(false)}
        buttonText={t('flightsSearch.backButton')}
      />
    </>
  );
};

export default FlightsExchangeSearch;
